import { useState } from "react";

import { Button } from "@components/Button";
import { Drawer } from "@components/Drawer";
import { Hairline } from "@components/Hairline";
import { Input } from "@components/Input";
import { Label } from "@components/Label";
import { Loading } from "@components/Loading";
import { Radio } from "@components/Radio";

import style from "@styles/pages/oraculo/oraculo.module.scss";

import { SuccessIcon } from "@components/Icons";
import { If } from "@components/control-statements";
import { Oraculo } from "@core/oraculo";
import OcdpAPI from "@providers/OcdpAPI";
import classNames from "classnames/bind";

const css = classNames.bind(style);

const MethodsSave = {
  SAVE: "SAVE",
  XLS: "XLS",
  EMAIL: "EMAIL",
  DRIVE: "DRIVE",
};

const AudienceType = {
  STATIC: "STATIC",
  DYNAMIC: "DYNAMIC",
};

const clone = (obj) => JSON.parse(JSON.stringify(obj));

const download = (filename) => {
  const a = document.createElement("a");
  a.setAttribute("href", filename);
  a.setAttribute("download", true);
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export default function UseAudience ({
  visible,
  execution_id = null,
  audience = null,
  state,
  onSave,
  onClose,
}) {
  const [selectedSave, setSelectedSave] = useState(null);
  const [finishedSave, setFinishedSave] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(audience?.name || "");
  const [audience_type, setAudienceType] = useState(
    audience?.type || AudienceType.STATIC
  );

  const getTitle = () => {
    if (finishedSave) return "Sucesso";

    const Titles = {
      SAVE: "Salvar público",
      XLS: "Exportar XLS",
      EMAIL: "Enviar por e-mail",
      DRIVE: "Salvar no drive",
    };

    return Titles[selectedSave] || "Usar público";
  };

  const save_fn = async () => {
    try {
      const ocdp_api = new OcdpAPI();
      setLoading(true);

      const _state = clone(state || {});
      const response = await ocdp_api.upsert_audience(audience?.id, {
        ...(_state || {}),
        type: audience_type,
        name: name,
      });

      if (!response?.execution_id) {
        throw new Error("Ocorreu um erro ao salvar o público");
      }

      const exec = await Oraculo.waitQueryExecution(response?.execution_id);
      if (exec?.status == "ERROR") {
        throw new Error("Ocorreu um erro ao salvar o público");
      }

      setLoading(false);
      setFinishedSave(true);
      return response;
    } catch (err) {
      alert({ text: err.message });
      setLoading(false);
    }
    return false;
  };

  const save_hub = async () => {
    switch (selectedSave) {
      case MethodsSave.SAVE: {
        const response = await save_fn();
        if (!response) {
          return false;
        }

        const ocdp_api = new OcdpAPI();
        const audience = await ocdp_api.audience(response?.audience_id);
        const { sort, limit } = audience?.extras || {};
        onSave && onSave({ ...audience, sort, limit });
        break;
      }
      case MethodsSave.XLS: {
        try {
          setLoading(true);
          const response = await Oraculo.export(execution_id, audience?.id, state?.filters || []);
          if (!response || response?.status == "ERROR") {
            throw new Error("Ocorreu um erro ao exportar o público");
          }
          download(response?.link);
          setLoading(false);
        } catch (err) {
          alert({ text: err.message });
          setLoading(false);
          return false;
        }
        break;
      }
      case MethodsSave.EMAIL: {
        break;
      }
      default: break;
    }
    setName("");
  };

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        onClose();
        setSelectedSave(null);
        setFinishedSave(false);
        setName("");
      }}
      title={getTitle()}
      subtitle={finishedSave && "Você já pode visualizar o seu público"}
      showCloseButton
      showBackButton={selectedSave && !finishedSave && !loading}
      backButton={() => setSelectedSave(null)}
    >
      <If cond={!loading}>
        <If cond={!selectedSave && !finishedSave}>
          <div className={style.optionsUseAudience}>
            <div
              className={css({ option: true, save: true })}
              onClick={() => setSelectedSave(MethodsSave.SAVE)}
            >
              <div className="d-flex align-center">
                <div className={style.icon}>
                  <i className="fal fa-notebook" />
                </div>
                <p>Salvar {audience?.id ? "edições" : "público"}</p>
              </div>
              <i className="fas fa-chevron-right" />
            </div>
            <div
              className={style.option}
              onClick={() => setSelectedSave(MethodsSave.XLS)}
            >
              <div className="d-flex align-center">
                <div className={style.icon}>
                  <i className="fal fa-download" />
                </div>
                <p>Exportar XLS</p>
              </div>
              <i className="fas fa-chevron-right" />
            </div>
            {/* <div
							className={style.option}
							onClick={() => setSelectedSave(MethodsSave.EMAIL)}
						>
							<div className="d-flex align-center">
								<div className={style.icon}>
									<i className="fal fa-at" />
								</div>
								<p>Enviar para e-mail</p>
							</div>
							<i className="fas fa-chevron-right" />
						</div>
						<div
							className={style.option}
						// onClick={() => setSelectedSave(MethodsSave.DRIVE)}
						>
							<div className="d-flex align-center">
								<div className={style.icon}>
									<i className="fab fa-google-drive" />
								</div>
								<p>Salvar no drive</p>
							</div>
							<i className="fas fa-chevron-right" />
						</div> */}
          </div>
        </If>

        <If cond={!finishedSave}>
          <If cond={selectedSave === MethodsSave.SAVE}>
            <Label>Dê um nome ao seu público:</Label>
            <Input
              placeholder="Nome do público"
              value={name}
              onChange={(text) => setName(text.target.value)}
            />
            <Hairline className="mt-30 mb-30" />
            <Label>Defina o tipo do público:</Label>
            <div className="d-flex align-center justify-between mb-10">
              <Radio
                family="audienceType"
                value={AudienceType.STATIC}
                checked={audience_type}
                onChange={() => setAudienceType(AudienceType.STATIC)}
              >
                Estático
              </Radio>
              <span className={style.tag}>FOTOGRAFIA DE AGORA</span>
            </div>
            <div className="d-flex align-center justify-between">
              <Radio
                family="audienceType"
                value={AudienceType.DYNAMIC}
                checked={audience_type}
                onChange={() => setAudienceType(AudienceType.DYNAMIC)}
              >
                Dinâmico
              </Radio>
              <span className={style.tag}>RECALCULADO DIARIAMENTE</span>
            </div>
          </If>

          <If cond={selectedSave === MethodsSave.XLS}>
            <Label>
              Você gostaria de realizar o download do arquivo XLS deste público?
            </Label>
            <Button
              className="mb-10 mt-20"
              onClick={save_hub}
              loading={loading}
              disabled={loading}
              design="primary"
            >
              Realizar download
            </Button>
            <Button
              design="two"
              onClick={() => setSelectedSave(null)}
              loading={loading}
              disabled={loading}
            >
              Cancelar
            </Button>
          </If>

          <If cond={selectedSave === MethodsSave.EMAIL}>
            <Label>Informe o endereço de e-mail:</Label>
            <Input placeholder="E-mail" />
          </If>
        </If>

        <If cond={finishedSave}>
          <div className={style.successContainer}>
            <div className={style.successIcon}>
              <SuccessIcon />
            </div>
          </div>
        </If>

        <If cond={selectedSave && !loading}>
          <div className={style.footer} onClick={save_hub}>
            <If
              cond={
                !finishedSave &&
                [MethodsSave.SAVE, MethodsSave.EMAIL].includes(selectedSave)
              }
            >
              <Button
                className="w-100"
                disabled={name.length < 3}
                onClick={save_hub}
                design="primary"
              >
                Salvar
              </Button>
            </If>
            <If cond={finishedSave}>
              <Button
                design="primary"
                className="w-100"
                text={"Concluir"}
                onClick={() => {
                  onClose();
                  setSelectedSave(null);
                  setFinishedSave(false);
                }}
              />
            </If>
          </div>
        </If>
      </If>

      <If cond={loading}>
        <div className={style.loading}>
          <Loading />
        </div>
      </If>
    </Drawer>
  );
};
